import type { FC } from 'react';
import { useState, memo, useCallback } from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { SvgIcon } from 'root/widgets/svg-icon';
import Popover from 'root/widgets/common-components/popover/popover';

import { BadgeVariant, VERIFIED_BADGE_EVENTS, type VerifiedBadgeProps } from './types';
import './verified-badge.scss';
import { BadgeButton, BadgeModal } from './sub-components';
import { HIVE_UI_CORE_URL, MARKETPLACE, SCREEN_BREAKPOINTS } from 'root/widgets/constants';
import Actionable from 'root/widgets/common-components/actionable';
import { useTooltip } from './hooks/use-tooltip';
import { extractDataAutomationId } from 'root/widgets/utils/automation';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { emit } from 'root/widgets/events';

const VerifiedBadge: FC<VerifiedBadgeProps> = ({
  icon,
  text,
  modalTitle,
  explanation,
  note,
  variant = BadgeVariant.AGENT,
  marketplace = MARKETPLACE.PG,
  popover,
  modalSize,
  trackingData,
  className,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    isBadgeOverlayVisible,
    isInfoTooltipVisible,
    handleBadgeOverlayToggle,
    handleBadgeOverlayClose,
    handleInfoTooltipClose,
    handleMouseOver,
  } = useTooltip(variant);

  const handleOpenModal = useCallback((): void => {
    if (isBadgeOverlayVisible) {
      handleBadgeOverlayToggle(false);
    }
    setIsModalOpen(true);
    emit(VERIFIED_BADGE_EVENTS.VERIFIED_BADGE_CLICK, { ...trackingData });
  }, [isBadgeOverlayVisible, handleBadgeOverlayToggle]);

  const handleCloseModal = useCallback((): void => setIsModalOpen(false), []);
  const isCoarsePointer = typeof window === 'undefined' ? false : window.matchMedia('(pointer: coarse)').matches;
  const shouldShowBottomModal = useMediaQuery({ maxWidth: SCREEN_BREAKPOINTS.MD });
  const isMobileDevice = isCoarsePointer ?? shouldShowBottomModal;

  return (
    <>
      <button
        type="button"
        className="verified-badge"
        onMouseOver={handleMouseOver}
        onFocus={() => {}}
        onClick={(e) => e.preventDefault()}
        aria-label={text}
      >
        <OverlayTrigger
          placement={popover.placement}
          rootClose
          trigger={isMobileDevice ? 'focus' : 'hover'}
          defaultShow={false}
          show={isBadgeOverlayVisible && variant === BadgeVariant.AGENT}
          onToggle={handleBadgeOverlayToggle}
          overlay={
            <Popover
              variant="dark"
              closeButton
              handleClose={handleBadgeOverlayClose}
              {...extractDataAutomationId({
                dataAutomationId: `${variant}-badge-tltip`,
              })}
              closeButtonAutomationId={`${variant}-badge-tltip-close-btn`}
              className={classNames('verified-badge-popover', className)}
            >
              {popover.title && <Popover.Header>{popover.title}</Popover.Header>}
              <Popover.Body>
                {popover.description}
                {popover.buttonLabel && (
                  <Actionable
                    size="sm"
                    variant="light"
                    href={popover.buttonUrl}
                    onClick={handleOpenModal}
                    metadata={{
                      dataAutomationId: `${variant}-badge-tltip-btn`,
                    }}
                  >
                    {popover.buttonLabel}
                  </Actionable>
                )}
              </Popover.Body>
            </Popover>
          }
        >
          <div>
            <BadgeButton
              variant={variant}
              marketplace={marketplace}
              icon={icon}
              text={text}
              onClick={handleOpenModal}
            />
          </div>
        </OverlayTrigger>
        {variant === BadgeVariant.LISTING && (
          <Actionable
            className="info-icon-btn"
            variant="link"
            onClick={handleOpenModal}
            metadata={{
              dataAutomationId: 'info-btn',
            }}
            popover={{
              placement: 'bottom',
              overlay: (
                <Popover
                  variant="dark"
                  closeButton
                  handleClose={handleInfoTooltipClose}
                  {...extractDataAutomationId({
                    dataAutomationId: 'info-tltip',
                  })}
                  closeButtonAutomationId="info-tltip-close-btn"
                  className={classNames('verified-badge-info-popover', className)}
                  onClick={handleOpenModal}
                >
                  {popover.title && <Popover.Header>{popover.title}</Popover.Header>}
                  <Popover.Body>
                    {popover.description}
                    {popover.buttonLabel && (
                      <Actionable
                        size="sm"
                        variant="light"
                        href={popover.buttonUrl}
                        metadata={{
                          dataAutomationId: `${variant}-badge-tltip-btn`,
                        }}
                      >
                        {popover.buttonLabel}
                      </Actionable>
                    )}
                  </Popover.Body>
                </Popover>
              ),
              delay: {
                show: 5000,
                hide: 0,
              },
              show: isInfoTooltipVisible,
            }}
          >
            <SvgIcon
              src={`${HIVE_UI_CORE_URL}icons/svgs/info-circle-o.svg`}
              width={16}
              height={16}
              title={text}
              className="info-icon"
            />
          </Actionable>
        )}
      </button>

      <BadgeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalTitle={modalTitle}
        variant={variant}
        marketplace={marketplace}
        icon={icon}
        text={text}
        explanation={explanation}
        note={note}
        size={modalSize}
        trackingData={trackingData}
        className={className}
      />
    </>
  );
};

export default memo(VerifiedBadge);
